/* ──────────────────────────────────────────────────────────────────────────
 * WorkspaceRefBar — a single, uniform reference strip mounted across every
 * module under the Dashboard (IPO Navigator, DRHP Workspace, Advisor
 * Marketplace, Document Vault). It always shows the same four reference points
 * so the user's context is consistent everywhere:
 *   Entity (Registered Legal Name) · User · Board (SME / Main Board) · Tentative filing date
 * Self-contained: fetches /api/workspace + /api/auth/me, scoped under
 * `.ws-refbar-root` so module CSS never bleeds in.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import "./WorkspaceRefBar.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const boardLabel = (t) => (t === "mb" ? "Main Board" : t === "sme" ? "SME" : "Not set yet");
const boardSub = (t) => (t === "mb" ? "NSE / BSE" : t === "sme" ? "BSE SME / NSE Emerge" : "");

const fmtDate = (iso) => {
  if (!iso) return "Not set yet";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export const WorkspaceRefBar = ({ className = "" }) => {
  const [ws, setWs] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/workspace`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => active && setWs(d))
      .catch(() => {});
    fetch(`${API_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => active && setMe(d))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const entity = (ws?.company?.legal_name || "").trim() || "Set up your company";
  const userName = (me?.name || me?.email || "—").trim();
  const lt = ws?.listing_type || "";
  const sub = boardSub(lt);

  const items = [
    { id: "entity", ic: "ti-building", k: "Entity", v: entity },
    { id: "user", ic: "ti-user-circle", k: "User", v: userName },
    { id: "board", ic: "ti-building-bank", k: "Board", v: boardLabel(lt) + (sub ? ` · ${sub}` : "") },
    { id: "filing-date", ic: "ti-calendar-event", k: "Tentative filing", v: fmtDate(ws?.target_filing_date) },
  ];

  return (
    <div className={`ws-refbar-root ${className}`} data-testid="workspace-ref-bar">
      <div className="ws-refbar">
        {items.map((it) => (
          <div className="wrb-item" key={it.id} data-testid={`wrb-${it.id}`} title={`${it.k}: ${it.v}`}>
            <span className="wrb-ic"><i className={`ti ${it.ic}`} aria-hidden="true" /></span>
            <span className="wrb-text">
              <span className="wrb-k">{it.k}</span>
              <span className="wrb-v">{it.v}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkspaceRefBar;
